import { apiClient } from './client';

/**
 * List games for a lesson.
 * @param {string} lessonId
 */
export async function getLessonGames(lessonId) {
  const { data } = await apiClient.get(`/lessons/${lessonId}/games`);
  return data;
}

/**
 * Create a game on a lesson.
 * @param {string} lessonId
 * @param {{ type: string, title: string, description?: string }} body
 */
export async function createGame(lessonId, body) {
  const { data } = await apiClient.post(`/lessons/${lessonId}/games`, body);
  return data;
}

/**
 * Update game title / description, or show/hide it to students (`isActive`).
 * @param {string} gameId
 * @param {{ title?: string, description?: string, isActive?: boolean }} body
 */
export async function updateGame(gameId, body) {
  const { data } = await apiClient.patch(`/games/${gameId}`, body);
  return data;
}

/**
 * Soft-delete a game.
 * @param {string} gameId
 */
export async function deleteGame(gameId) {
  await apiClient.delete(`/games/${gameId}`);
}

/**
 * Staff read of a game's saved content: story steps (each with `choices`) or puzzles (with answers).
 * @param {string} gameId
 * @returns {Promise<{ id, lessonId, name, gameType, isActive, source, nodes: object[], puzzles: object[] }>}
 */
export async function getGameContent(gameId) {
  const { data } = await apiClient.get(`/games/${gameId}/content`);
  return data;
}

/**
 * Ask AI to write a story or puzzle game for a lesson (MENTOR and ADMIN only).
 * @param {string} lessonId
 * @param {{ gameType: 'STORY_ADVENTURE'|'PUZZLE_MASTER', topic: string }} body
 * @returns {Promise<{ taskId: string, status: 'pending', gameId: string, requestedAt: string }>}
 *   `gameId` is the request: poll getGenerationStatus with it.
 */
export async function generateGame(lessonId, body) {
  const { data } = await apiClient.post(`/lessons/${lessonId}/games/generate`, body);
  return data;
}

/**
 * Where an AI request stands. On `ready`, `gameId` is the new game (hidden from students).
 * @param {string} requestGameId - the gameId generateGame returned
 * @returns {Promise<{ status: 'pending'|'ready'|'failed', gameId?: string }>}
 */
export async function getGenerationStatus(requestGameId) {
  const { data } = await apiClient.get(`/games/${requestGameId}/generation`);
  return data;
}

/**
 * Per lesson of a course: words, pictures, example sentences and which word games are open.
 * @param {string} courseId
 * @returns {Promise<{ courseId, rules: object, lessons: object[] }>}
 */
export async function getArcadeReadiness(courseId) {
  const { data } = await apiClient.get(`/games/arcade/courses/${courseId}/readiness`);
  return data;
}

// ---- STORY_ADVENTURE ----

/**
 * Add a node to a STORY_ADVENTURE game.
 * @param {string} gameId
 * @param {{ type: 'PROMPT'|'TERMINAL', text: string, scoreOnReach?: number, isStartNode?: boolean }} body
 */
export async function addGameNode(gameId, body) {
  const { data } = await apiClient.post(`/games/${gameId}/nodes`, body);
  return data;
}

/**
 * Add a choice edge from a node.
 * @param {string} gameId
 * @param {string} nodeId
 * @param {{ label: string, toNodeId: string, orderIndex?: number }} body
 */
export async function addNodeChoice(gameId, nodeId, body) {
  const { data } = await apiClient.post(`/games/${gameId}/nodes/${nodeId}/choices`, body);
  return data;
}

/**
 * Edit a saved step. `isStartNode: true` makes it the first step (the old one stops being it).
 * @param {string} gameId
 * @param {string} nodeId
 * @param {{ type?: 'PROMPT'|'TERMINAL', text?: string, scoreOnReach?: number, isStartNode?: true }} body
 */
export async function updateGameNode(gameId, nodeId, body) {
  const { data } = await apiClient.patch(`/games/${gameId}/nodes/${nodeId}`, body);
  return data;
}

/**
 * Delete a step and every choice to or from it.
 * @returns {Promise<{ id, deleted: true, removedChoices: number, startNodeId: string|null }>}
 */
export async function deleteGameNode(gameId, nodeId) {
  const { data } = await apiClient.delete(`/games/${gameId}/nodes/${nodeId}`);
  return data;
}

/**
 * Edit a choice: its label, the step it leads to, its order.
 * @param {{ label?: string, toNodeId?: string, orderIndex?: number }} body
 */
export async function updateNodeChoice(gameId, choiceId, body) {
  const { data } = await apiClient.patch(`/games/${gameId}/choices/${choiceId}`, body);
  return data;
}

export async function deleteNodeChoice(gameId, choiceId) {
  const { data } = await apiClient.delete(`/games/${gameId}/choices/${choiceId}`);
  return data;
}

// ---- PUZZLE_MASTER ----

/**
 * Add a puzzle to a PUZZLE_MASTER game.
 * @param {string} gameId
 * @param {{ prompt: string, answer: string, hints?: string[], scoreOnCorrect?: number, orderIndex?: number }} body
 */
export async function addPuzzle(gameId, body) {
  const { data } = await apiClient.post(`/games/${gameId}/puzzles`, body);
  return data;
}

/**
 * Add up to 100 puzzles at once, after the last one (all or none).
 * @param {string} gameId
 * @param {Array<{ prompt: string, answer: string, hints?: string[] }>} puzzles
 * @returns {Promise<{ created: number, puzzles: object[] }>}
 */
export async function addPuzzlesBulk(gameId, puzzles) {
  const { data } = await apiClient.post(`/games/${gameId}/puzzles/bulk`, { puzzles });
  return data;
}

/**
 * Edit a saved puzzle.
 * @param {{ prompt?: string, answer?: string, hints?: string[], scoreOnCorrect?: number }} body
 */
export async function updatePuzzle(gameId, puzzleId, body) {
  const { data } = await apiClient.patch(`/games/${gameId}/puzzles/${puzzleId}`, body);
  return data;
}

export async function deletePuzzle(gameId, puzzleId) {
  const { data } = await apiClient.delete(`/games/${gameId}/puzzles/${puzzleId}`);
  return data;
}

// ---- SIMULATOR ----

/**
 * List simulator scenarios for a game.
 * @param {string} gameId
 */
export async function getSimulatorScenarios(gameId) {
  const { data } = await apiClient.get(`/games/${gameId}/simulator/scenarios`);
  return data;
}

/**
 * Create a simulator scenario on a game.
 * @param {string} gameId
 * @param {{ title: string, description?: string }} body
 */
export async function createSimulatorScenario(gameId, body) {
  const { data } = await apiClient.post(`/games/${gameId}/simulator/scenarios`, body);
  return data;
}

/**
 * Add a step to a simulator scenario.
 * @param {string} gameId
 * @param {string} scenarioId
 * @param {{ prompt: string, expectedAction: string, feedback: string, scoreOnCorrect?: number, order?: number }} body
 */
export async function addSimulatorStep(gameId, scenarioId, body) {
  const { data } = await apiClient.post(
    `/games/${gameId}/simulator/scenarios/${scenarioId}/steps`,
    body,
  );
  return data;
}

// ---- SKILL_QUEST ----

/**
 * List quest nodes for a game.
 * @param {string} gameId
 */
export async function getQuestNodes(gameId) {
  const { data } = await apiClient.get(`/games/${gameId}/quest/nodes`);
  return data;
}

/**
 * Create a quest node on a game.
 * @param {string} gameId
 * @param {{ title: string, description?: string, type: 'CHALLENGE'|'CHECKPOINT'|'BOSS', requiredScore?: number, order?: number }} body
 */
export async function createQuestNode(gameId, body) {
  const { data } = await apiClient.post(`/games/${gameId}/quest/nodes`, body);
  return data;
}

/**
 * Get the skill tree for a game.
 * @param {string} gameId
 */
export async function getSkillTree(gameId) {
  const { data } = await apiClient.get(`/games/${gameId}/quest/skill-tree`);
  return data;
}
